/**
 * Client-safe image path helpers (no Node filesystem APIs).
 * Keep these free of server-only imports so UI components can use them.
 */

/**
 * Environment segment for local media folders (dev | prod | …).
 * Set NEXT_PUBLIC_MEDIA_ENV so uploads + display stay isolated per env.
 * Files live at public/prajavarta/{env}/{folder}/…
 * URLs /prajavarta/{env}/{folder}/… are served at runtime via /api/media (see next.config rewrites).
 * `npm run build` stashes public/prajavarta so those files are not part of the build.
 */
export function getMediaEnv() {
    const raw = String(process.env.NEXT_PUBLIC_MEDIA_ENV || 'dev').trim().toLowerCase()
    const safe = raw.replace(/[^a-z0-9_-]/g, '')
    return safe || 'dev'
}

/** Root folder: prajavarta/dev or prajavarta/prod */
export function getMediaRootPath() {
    return `prajavarta/${getMediaEnv()}`
}

/** Full folder path: prajavarta/dev/news */
export function getMediaFolderPath(folder) {
    const root = getMediaRootPath()
    return folder ? `${root}/${folder}` : root
}

/**
 * Value stored in DB — basename only (e.g. abc123.jpg), never a full URL.
 */
export function getStoredImageName(value) {
    if (!value) return null
    const raw = String(value).trim()
    if (!raw || raw.startsWith('blob:') || raw.startsWith('data:')) return null

    let path = raw
    try {
        if (/^https?:\/\//i.test(raw)) {
            path = new URL(raw).pathname
        }
    } catch {
        // keep raw
    }

    const segments = path.split('/').filter(Boolean)
    if (!segments.length) return null
    return segments[segments.length - 1].split('?')[0] || null
}

/**
 * Build a displayable image URL from a stored basename or legacy full URL.
 * Local files: /prajavarta/{env}/{folder}/{basename}
 * Absolute http(s) URLs and paths starting with / are returned unchanged.
 *
 * @param {string} value - Basename or URL
 * @param {string} folder - Type folder under prajavarta/{env}/ (news, authors, …)
 * @param {{ width?: number, height?: number, crop?: string, gravity?: string, quality?: string }} [options]
 */
/** Normalize DB/API image fields before {@link resolveImageUrl}. */
export function sanitizePublicImageSrc(value) {
    if (value == null) return null
    const raw = String(value).trim()
    if (!raw || raw === 'null' || raw === 'undefined' || raw === 'None') return null
    return raw
}

export function resolveImageUrl(value, folder = 'news', _options = {}) {
    const sanitized = sanitizePublicImageSrc(value)
    if (!sanitized) return null
    const raw = sanitized

    if (raw.startsWith('blob:') || raw.startsWith('data:')) {
        return raw
    }

    if (/^https?:\/\//i.test(raw)) {
        return raw
    }

    if (raw.startsWith('/')) {
        return raw
    }

    const folderPath = getMediaFolderPath(folder)
    return `/${folderPath}/${raw}`
}

/**
 * Extract storage id from a local public URL (prajavarta/…).
 * @param {string} secureUrl
 * @returns {string|null}
 */
export function extractPublicIdFromUrl(secureUrl) {
    if (!secureUrl) return null
    const match = secureUrl.match(/\/prajavarta\/(.+?)(?:\.[^/?]+)?(?:\?|$)/)
    return match ? `prajavarta/${match[1]}` : null
}

/** Storage public_id for delete operations (prajavarta/{env}/{folder}/{stem}). */
export function toMediaPublicId(value, folder = 'news') {
    if (!value) return null
    const raw = String(value).trim()
    if (!raw) return null

    if (/^https?:\/\//i.test(raw)) {
        return extractPublicIdFromUrl(raw)
    }

    const name = getStoredImageName(raw)
    if (!name) return null
    const withoutExt = name.replace(/\.[^/.]+$/, '')
    return `${getMediaFolderPath(folder)}/${withoutExt}`
}
